import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { auth } from '../../firebase.js';
import '../../css/ConvertDocumentModal.css';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

/**
 * ConvertDocumentModal
 *
 * Props:
 *   isOpen              – boolean
 *   onClose             – () => void
 *   preSelectedDirection – 'personal_to_workspace' | 'workspace_to_personal' | null
 *   preSelectedWorkspaceId – number | null  (source workspace when workspace_to_personal)
 */
function ConvertDocumentModal({
    isOpen,
    onClose,
    preSelectedDirection = null,
    preSelectedWorkspaceId = null,
}) {
    const [uid, setUid] = useState('');

    // Wizard state: 'configure' | 'parent_assignment' | 'child_action' | 'results'
    const [view, setView] = useState('configure');
    const [direction, setDirection] = useState(preSelectedDirection || 'personal_to_workspace');
    const [activeTab, setActiveTab] = useState('spaces');

    // Source document data
    const [workspaces, setWorkspaces] = useState([]);
    const [personalSpaces, setPersonalSpaces] = useState([]);
    const [personalPages, setPersonalPages] = useState([]);
    const [wsSpaces, setWsSpaces] = useState([]);
    const [wsPages, setWsPages] = useState([]);

    // Target document data (for parent assignment)
    const [targetWsSpaces, setTargetWsSpaces] = useState([]);
    const [targetWsPages, setTargetWsPages] = useState([]);
    const [loadingTargetDocs, setLoadingTargetDocs] = useState(false);

    // Selections
    const [sourceWorkspaceId, setSourceWorkspaceId] = useState(preSelectedWorkspaceId || '');
    const [targetWorkspaceId, setTargetWorkspaceId] = useState('');
    const [selectedDocs, setSelectedDocs] = useState([]);

    // Options
    const [deleteOriginals, setDeleteOriginals] = useState(false);

    // Parent assignment step: { [pageId]: { parentType: 'space'|'page'|null, parentId: number|null } }
    const [pageParentAssignments, setPageParentAssignments] = useState({});

    // Child action step
    const [docsWithChildren, setDocsWithChildren] = useState([]);
    const [childAction, setChildAction] = useState('');
    const [checkingChildren, setCheckingChildren] = useState(false);

    // UI state
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [converting, setConverting] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState('');

    /* ── Auth ── */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged((user) => {
            if (user) setUid(user.uid);
        });
        return unsub;
    }, []);

    /* ── Reset on open ── */
    useEffect(() => {
        if (isOpen) {
            setView('configure');
            setDirection(preSelectedDirection || 'personal_to_workspace');
            setSourceWorkspaceId(preSelectedWorkspaceId || '');
            setTargetWorkspaceId('');
            setSelectedDocs([]);
            setDeleteOriginals(false);
            setResults(null);
            setError('');
            setActiveTab('spaces');
            setDocsWithChildren([]);
            setChildAction('');
            setPageParentAssignments({});
            setTargetWsSpaces([]);
            setTargetWsPages([]);
        }
    }, [isOpen, preSelectedDirection, preSelectedWorkspaceId]);

    /* ── Helpers ── */
    const getHeaders = useCallback(async () => {
        const user = auth.currentUser;
        if (!user) return {};
        const token = await user.getIdToken();
        return {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
            'Auth-Token': `Bearer ${token}`,
            'Requesting-User-Uid': user.uid,
        };
    }, []);

    /* ── Fetch workspaces ── */
    useEffect(() => {
        if (!uid || !isOpen) return;
        (async () => {
            try {
                const headers = await getHeaders();
                const res = await fetch(`${API_BASE}/workspaces/`, { headers });
                if (res.ok) {
                    const data = await res.json();
                    const seen = new Map();
                    for (const ws of data) {
                        if (!seen.has(ws.id) || ws.role === 'OWNER') seen.set(ws.id, ws);
                    }
                    setWorkspaces(Array.from(seen.values()));
                }
            } catch (e) {
                console.error('Error fetching workspaces:', e);
            }
        })();
    }, [uid, isOpen, getHeaders]);

    /* ── Fetch personal docs ── */
    // Needed in both directions: as the source list when personal_to_workspace,
    // and as the destination parent list when workspace_to_personal (see destinationSpaces/Pages).
    useEffect(() => {
        if (!uid || !isOpen) return;
        (async () => {
            setLoadingDocs(true);
            try {
                const headers = await getHeaders();
                const [sRes, pRes] = await Promise.all([
                    fetch(`${API_BASE}/spaces/author/${uid}/`, { headers }),
                    fetch(`${API_BASE}/pages/author/${uid}/`, { headers }),
                ]);
                setPersonalSpaces(sRes.ok ? (await sRes.json()).spaces || [] : []);
                setPersonalPages(pRes.ok ? (await pRes.json()).pages || [] : []);
            } catch (e) {
                console.error('Error fetching personal documents:', e);
            } finally {
                setLoadingDocs(false);
            }
        })();
    }, [uid, isOpen, getHeaders]);

    /* ── Fetch source workspace docs when workspace_to_personal and source selected ── */
    useEffect(() => {
        if (!uid || !isOpen || direction !== 'workspace_to_personal' || !sourceWorkspaceId) return;
        (async () => {
            setLoadingDocs(true);
            setWsSpaces([]);
            setWsPages([]);
            try {
                const headers = await getHeaders();
                const [sRes, pRes] = await Promise.all([
                    fetch(`${API_BASE}/workspaces/${sourceWorkspaceId}/spaces/`, { headers }),
                    fetch(`${API_BASE}/workspaces/${sourceWorkspaceId}/pages/`, { headers }),
                ]);
                setWsSpaces(sRes.ok ? (await sRes.json()) : []);
                setWsPages(pRes.ok ? (await pRes.json()) : []);
            } catch (e) {
                console.error('Error fetching workspace documents:', e);
            } finally {
                setLoadingDocs(false);
            }
        })();
    }, [uid, isOpen, direction, sourceWorkspaceId, getHeaders]);

    /* ── Fetch target workspace docs when personal_to_workspace and target selected ── */
    useEffect(() => {
        if (!uid || !isOpen || direction !== 'personal_to_workspace' || !targetWorkspaceId) return;
        (async () => {
            setLoadingTargetDocs(true);
            try {
                const headers = await getHeaders();
                const [sRes, pRes] = await Promise.all([
                    fetch(`${API_BASE}/workspaces/${targetWorkspaceId}/spaces/`, { headers }),
                    fetch(`${API_BASE}/workspaces/${targetWorkspaceId}/pages/`, { headers }),
                ]);
                setTargetWsSpaces(sRes.ok ? (await sRes.json()) : []);
                setTargetWsPages(pRes.ok ? (await pRes.json()) : []);
            } catch (e) {
                console.error('Error fetching target workspace documents:', e);
            } finally {
                setLoadingTargetDocs(false);
            }
        })();
    }, [uid, isOpen, direction, targetWorkspaceId, getHeaders]);

    /* ── Document selection helpers ── */
    const docKey = (d) => `${d.type}-${d.id}`;
    const isSelected = (doc) => selectedDocs.some((d) => docKey(d) === docKey(doc));

    const toggleDoc = (doc) => {
        setSelectedDocs((prev) =>
            isSelected(doc) ? prev.filter((d) => docKey(d) !== docKey(doc)) : [...prev, doc]
        );
    };

    const selectAll = () => {
        const spaces = direction === 'personal_to_workspace' ? personalSpaces : wsSpaces;
        const pages = direction === 'personal_to_workspace' ? personalPages : wsPages;
        const all = [
            ...spaces.map((s) => ({ type: 'space', id: s.id, name: s.name })),
            ...pages.map((p) => ({ type: 'page', id: p.id, name: p.page_name })),
        ];
        setSelectedDocs((prev) => {
            const existing = new Set(prev.map(docKey));
            return [...prev, ...all.filter((d) => !existing.has(docKey(d)))];
        });
    };

    const clearAll = () => setSelectedDocs([]);

    /* ── Direction change ── */
    const handleDirectionChange = (d) => {
        setDirection(d);
        setSelectedDocs([]);
        setSourceWorkspaceId(d === 'workspace_to_personal' ? (preSelectedWorkspaceId || '') : '');
        setTargetWorkspaceId('');
        setTargetWsSpaces([]);
        setTargetWsPages([]);
        setError('');
    };

    /* ── Validation ── */
    const validate = () => {
        if (selectedDocs.length === 0) {
            setError('Please select at least one document to convert.');
            return false;
        }
        if (direction === 'personal_to_workspace' && !targetWorkspaceId) {
            setError('Please select a target workspace.');
            return false;
        }
        if (direction === 'workspace_to_personal' && !sourceWorkspaceId) {
            setError('Please select a source workspace.');
            return false;
        }
        return true;
    };

    /* ── Step: configure → parent_assignment (if pages selected) or children check ── */
    const handleNextOrConvert = () => {
        if (!validate()) return;
        setError('');
        const selectedPages = selectedDocs.filter((d) => d.type === 'page');
        if (selectedPages.length > 0) {
            // Initialise assignments map so every page has an entry (null = no selection yet)
            const initial = {};
            selectedPages.forEach((p) => {
                initial[p.id] = pageParentAssignments[p.id] || { parentType: null, parentId: null };
            });
            setPageParentAssignments(initial);
            setView('parent_assignment');
        } else {
            checkChildrenThenProceed();
        }
    };

    /* ── Step: parent_assignment → children check ── */
    const handleParentAssignmentNext = () => {
        // All pages must have a real parent selected (standalone is not allowed)
        const selectedPages = selectedDocs.filter((d) => d.type === 'page');
        const unassigned = selectedPages.filter(
            (p) => !pageParentAssignments[p.id] ||
                   pageParentAssignments[p.id].parentType === undefined ||
                   pageParentAssignments[p.id].parentType === null
        );
        if (unassigned.length > 0) {
            setError('A parent document is required for every page. Please select a parent for each one.');
            return;
        }
        setError('');
        checkChildrenThenProceed();
    };

    /* ── Check children, then go to child_action or convert ── */
    const checkChildrenThenProceed = async () => {
        setCheckingChildren(true);
        setError('');
        try {
            const headers = await getHeaders();
            const payload = {
                direction,
                documents: selectedDocs.map((d) => ({ type: d.type, id: d.id })),
                ...(direction === 'workspace_to_personal' && { source_workspace_id: Number(sourceWorkspaceId) }),
            };
            const res = await fetch(`${API_BASE}/documentation/check-document-children/`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            const withChildren = data.documents_with_children || [];
            if (withChildren.length > 0) {
                setDocsWithChildren(withChildren);
                setChildAction('');
                setView('child_action');
            } else {
                await executeConvert('orphan');
            }
        } catch (e) {
            setError('An unexpected error occurred. Please try again.');
            console.error(e);
        } finally {
            setCheckingChildren(false);
        }
    };

    /* ── Execute the actual conversion ── */
    const executeConvert = async (resolvedChildAction) => {
        setConverting(true);
        setError('');
        try {
            const headers = await getHeaders();
            const payload = {
                direction,
                documents: selectedDocs.map((d) => ({
                    type: d.type,
                    id: d.id,
                    ...(direction === 'workspace_to_personal' && { workspace_id: Number(sourceWorkspaceId) }),
                })),
                delete_originals: deleteOriginals,
                child_action: resolvedChildAction,
                page_parent_assignments: Object.entries(pageParentAssignments).map(([pageId, a]) => ({
                    page_id: Number(pageId),
                    parent_type: a.parentType,
                    parent_id: a.parentId,
                })),
                ...(direction === 'personal_to_workspace' && { target_workspace_id: Number(targetWorkspaceId) }),
            };

            const res = await fetch(`${API_BASE}/documentation/convert-documents/`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
            });
            const data = await res.json();

            if (res.ok) {
                setResults(data);
                setView('results');
            } else {
                setError(data.error || 'Conversion failed. Please try again.');
            }
        } catch (e) {
            setError('An unexpected error occurred. Please try again.');
            console.error(e);
        } finally {
            setConverting(false);
        }
    };

    const handleConfirmChildAction = () => {
        if (!childAction) {
            setError('Please choose what to do with child documents.');
            return;
        }
        executeConvert(childAction);
    };

    const handleClose = () => {
        if (results && results.success_count > 0) window.location.reload();
        onClose();
    };

    /* ── Parent assignment helpers ── */
    const setPageParent = (pageId, parentType, parentId) => {
        setPageParentAssignments((prev) => ({
            ...prev,
            [pageId]: { parentType, parentId },
        }));
    };

    // Available parents in destination depending on direction
    const destinationSpaces = direction === 'personal_to_workspace' ? targetWsSpaces : personalSpaces;
    const destinationPages  = direction === 'personal_to_workspace' ? targetWsPages  : personalPages;

    const getParentSelectValue = (pageId) => {
        const a = pageParentAssignments[pageId];
        if (!a || a.parentType === undefined || a.parentType === null) return '__unset__';
        return `${a.parentType}-${a.parentId}`;
    };

    const handleParentSelectChange = (pageId, value) => {
        if (value === '__unset__') return;
        const [type, id] = value.split('-');
        setPageParent(pageId, type, Number(id));
    };

    if (!isOpen) return null;

    const currentSpaces = direction === 'personal_to_workspace' ? personalSpaces : wsSpaces;
    const currentPages  = direction === 'personal_to_workspace' ? personalPages  : wsPages;
    const isPersonalToWs = direction === 'personal_to_workspace';
    const selectedPageDocs = selectedDocs.filter((d) => d.type === 'page');

    return ReactDOM.createPortal(
        <div
            className="convert-modal-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
            <div className="convert-modal-content">
                {/* ── Header ── */}
                <div className="convert-modal-header">
                    <div className="convert-modal-title">
                        <i className="fas fa-exchange-alt"></i>
                        <h2>Convert Documents</h2>
                    </div>
                    <button className="convert-modal-close" onClick={handleClose} title="Close">×</button>
                </div>

                {view === 'results' ? (
                    /* ── Results view ── */
                    <>
                        <div className="convert-modal-body">
                            <div className="convert-results-summary">
                                <div className={`convert-results-stat ${results.success_count > 0 ? 'success' : ''}`}>
                                    <i className="fas fa-check-circle"></i>
                                    <span>{results.success_count} converted successfully</span>
                                </div>
                                {results.failed_count > 0 && (
                                    <div className="convert-results-stat error">
                                        <i className="fas fa-times-circle"></i>
                                        <span>{results.failed_count} failed</span>
                                    </div>
                                )}
                            </div>
                            <div className="convert-results-list">
                                {results.results.map((r, i) => (
                                    <div key={i} className={`convert-result-item ${r.success ? 'success' : 'error'}`}>
                                        <i className={`fas ${r.success ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                        <div className="convert-result-info">
                                            <span className="convert-result-name">
                                                {r.original_name || `Document #${r.original_id}`}
                                            </span>
                                            {r.success ? (
                                                <span className="convert-result-status">
                                                    Converted to {r.new_type.replace(/_/g, ' ')}
                                                </span>
                                            ) : (
                                                <span className="convert-result-error">{r.error}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="convert-modal-footer">
                            <button className="convert-btn primary" onClick={handleClose}>Done</button>
                        </div>
                    </>

                ) : view === 'parent_assignment' ? (
                    /* ── Parent assignment step ── */
                    <>
                        <div className="convert-modal-body">
                            <div className="convert-child-action-intro">
                                <i className="fas fa-code-branch"></i>
                                <p>
                                    Pages need a parent document in the destination.
                                    Choose where each page should live, or mark it as standalone.
                                </p>
                            </div>

                            {loadingTargetDocs ? (
                                <div className="convert-loading">
                                    <i className="fas fa-spinner fa-spin"></i> Loading destination documents…
                                </div>
                            ) : (
                                <div className="convert-parent-assignment-list">
                                    {selectedPageDocs.map((pageDoc) => {
                                        const value = getParentSelectValue(pageDoc.id);
                                        const isAssigned = value !== '__unset__';
                                        return (
                                            <div
                                                key={pageDoc.id}
                                                className={`convert-parent-assignment-item ${isAssigned ? 'assigned' : ''}`}
                                            >
                                                <div className="convert-parent-assignment-name">
                                                    <i className="fas fa-file-alt"></i>
                                                    <span>{pageDoc.name}</span>
                                                    {!isAssigned && (
                                                        <span className="convert-parent-required-badge">
                                                            Choose parent
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="convert-parent-select-row">
                                                    <select
                                                        className="convert-workspace-select"
                                                        value={value}
                                                        onChange={(e) => handleParentSelectChange(pageDoc.id, e.target.value)}
                                                    >
                                                        <option value="__unset__">— Select a parent —</option>
                                                        {destinationSpaces.length > 0 && (
                                                            <optgroup label="Spaces">
                                                                {destinationSpaces.map((s) => (
                                                                    <option key={`space-${s.id}`} value={`space-${s.id}`}>
                                                                        {s.name}
                                                                    </option>
                                                                ))}
                                                            </optgroup>
                                                        )}
                                                        {destinationPages.filter((p) =>
                                                            (p.page_name || p.page_name) !== pageDoc.name
                                                        ).length > 0 && (
                                                            <optgroup label="Pages">
                                                                {destinationPages
                                                                    .filter((p) => (p.page_name) !== pageDoc.name)
                                                                    .map((p) => (
                                                                        <option key={`page-${p.id}`} value={`page-${p.id}`}>
                                                                            {p.page_name}
                                                                        </option>
                                                                    ))}
                                                            </optgroup>
                                                        )}
                                                    </select>
                                                    {isAssigned && (
                                                        <i className="fas fa-check-circle convert-parent-assigned-icon"></i>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {error && <div className="convert-error">{error}</div>}
                        </div>

                        <div className="convert-modal-footer">
                            <button
                                className="convert-btn secondary"
                                onClick={() => { setView('configure'); setError(''); }}
                                disabled={checkingChildren}
                            >
                                <i className="fas fa-arrow-left"></i> Back
                            </button>
                            <button
                                className="convert-btn primary"
                                onClick={handleParentAssignmentNext}
                                disabled={checkingChildren || loadingTargetDocs}
                            >
                                {checkingChildren ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Checking…</>
                                ) : (
                                    <><i className="fas fa-arrow-right"></i> Next</>
                                )}
                            </button>
                        </div>
                    </>

                ) : view === 'child_action' ? (
                    /* ── Child action step ── */
                    <>
                        <div className="convert-modal-body">
                            <div className="convert-child-action-intro">
                                <i className="fas fa-sitemap"></i>
                                <p>
                                    The following documents have child pages. How would you like to handle them?
                                </p>
                            </div>

                            <div className="convert-child-docs-list">
                                {docsWithChildren.map((doc) => (
                                    <div key={`${doc.type}-${doc.id}`} className="convert-child-doc-item">
                                        <i className={`fas ${doc.type === 'space' ? 'fa-folder' : 'fa-file-alt'}`}></i>
                                        <span className="convert-child-doc-name">{doc.name}</span>
                                        <span className="convert-child-doc-count">
                                            {doc.child_count} child page{doc.child_count !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="convert-child-options">
                                <span className="convert-section-label">What should happen to the child pages?</span>

                                <label className={`convert-child-option ${childAction === 'convert' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="childAction"
                                        value="convert"
                                        checked={childAction === 'convert'}
                                        onChange={() => setChildAction('convert')}
                                    />
                                    <div>
                                        <span className="convert-child-option-title">
                                            <i className="fas fa-exchange-alt"></i> Convert children too
                                        </span>
                                        <span className="convert-child-option-hint">
                                            Child pages will also be converted and linked to their parent in the destination.
                                        </span>
                                    </div>
                                </label>

                                {isPersonalToWs && (
                                    <label className={`convert-child-option ${childAction === 'orphan' ? 'active' : ''}`}>
                                        <input
                                            type="radio"
                                            name="childAction"
                                            value="orphan"
                                            checked={childAction === 'orphan'}
                                            onChange={() => setChildAction('orphan')}
                                        />
                                        <div>
                                            <span className="convert-child-option-title">
                                                <i className="fas fa-unlink"></i> Leave as orphans
                                            </span>
                                            <span className="convert-child-option-hint">
                                                Child pages stay in your personal documents. You can assign them a new parent later via Check Data Integrity.
                                            </span>
                                        </div>
                                    </label>
                                )}

                                <label className={`convert-child-option convert-child-option--danger ${childAction === 'delete' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="childAction"
                                        value="delete"
                                        checked={childAction === 'delete'}
                                        onChange={() => setChildAction('delete')}
                                    />
                                    <div>
                                        <span className="convert-child-option-title">
                                            <i className="fas fa-trash"></i> Delete child pages
                                        </span>
                                        <span className="convert-child-option-hint">
                                            Child pages will be permanently deleted. This cannot be undone.
                                        </span>
                                    </div>
                                </label>
                            </div>

                            {error && <div className="convert-error">{error}</div>}
                        </div>

                        <div className="convert-modal-footer">
                            <button
                                className="convert-btn secondary"
                                onClick={() => {
                                    setView(selectedPageDocs.length > 0 ? 'parent_assignment' : 'configure');
                                    setError('');
                                }}
                                disabled={converting}
                            >
                                <i className="fas fa-arrow-left"></i> Back
                            </button>
                            <button
                                className="convert-btn primary"
                                onClick={handleConfirmChildAction}
                                disabled={converting || !childAction}
                            >
                                {converting ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Converting…</>
                                ) : (
                                    <><i className="fas fa-exchange-alt"></i> Convert</>
                                )}
                            </button>
                        </div>
                    </>

                ) : (
                    /* ── Configure + Select view ── */
                    <>
                        <div className="convert-modal-body">
                            {/* Direction */}
                            <div>
                                <span className="convert-section-label">Conversion Direction</span>
                                <div className="convert-direction-cards">
                                    <div
                                        className={`convert-direction-card ${direction === 'personal_to_workspace' ? 'active' : ''}`}
                                        onClick={() => handleDirectionChange('personal_to_workspace')}
                                    >
                                        <i className="fas fa-user"></i>
                                        <span>Personal</span>
                                        <i className="fas fa-arrow-right convert-arrow"></i>
                                        <i className="fas fa-users"></i>
                                        <span>Workspace</span>
                                    </div>
                                    <div
                                        className={`convert-direction-card ${direction === 'workspace_to_personal' ? 'active' : ''}`}
                                        onClick={() => handleDirectionChange('workspace_to_personal')}
                                    >
                                        <i className="fas fa-users"></i>
                                        <span>Workspace</span>
                                        <i className="fas fa-arrow-right convert-arrow"></i>
                                        <i className="fas fa-user"></i>
                                        <span>Personal</span>
                                    </div>
                                </div>
                            </div>

                            {/* Source workspace (workspace → personal) */}
                            {direction === 'workspace_to_personal' && (
                                <div className="convert-workspace-section">
                                    <span className="convert-section-label">Source Workspace</span>
                                    <select
                                        className="convert-workspace-select"
                                        value={sourceWorkspaceId}
                                        onChange={(e) => {
                                            setSourceWorkspaceId(e.target.value);
                                            setSelectedDocs([]);
                                        }}
                                    >
                                        <option value="">Select a workspace…</option>
                                        {workspaces.map((ws) => (
                                            <option key={ws.id} value={ws.id}>{ws.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Document selection */}
                            <div className="convert-documents-section">
                                <div className="convert-documents-header">
                                    <span className="convert-section-label">Select Documents</span>
                                    <div className="convert-docs-actions">
                                        <button className="convert-docs-action-btn" onClick={selectAll}>Select All</button>
                                        <button className="convert-docs-action-btn" onClick={clearAll}>Clear</button>
                                    </div>
                                </div>

                                {loadingDocs ? (
                                    <div className="convert-loading">
                                        <i className="fas fa-spinner fa-spin"></i> Loading…
                                    </div>
                                ) : direction === 'workspace_to_personal' && !sourceWorkspaceId ? (
                                    <div className="convert-empty">Select a workspace above to see its documents.</div>
                                ) : (
                                    <>
                                        <div className="convert-doc-tabs">
                                            <button
                                                className={`convert-doc-tab ${activeTab === 'spaces' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('spaces')}
                                            >
                                                <i className="fas fa-folder"></i>
                                                Spaces ({currentSpaces.length})
                                            </button>
                                            <button
                                                className={`convert-doc-tab ${activeTab === 'pages' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('pages')}
                                            >
                                                <i className="fas fa-file-alt"></i>
                                                Pages ({currentPages.length})
                                            </button>
                                        </div>

                                        <div className="convert-doc-list">
                                            {activeTab === 'spaces' ? (
                                                currentSpaces.length === 0 ? (
                                                    <p className="convert-empty">No spaces found.</p>
                                                ) : (
                                                    currentSpaces.map((space) => {
                                                        const doc = { type: 'space', id: space.id, name: space.name };
                                                        return (
                                                            <div
                                                                key={space.id}
                                                                className={`convert-doc-item ${isSelected(doc) ? 'selected' : ''}`}
                                                                onClick={() => toggleDoc(doc)}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    className="convert-doc-checkbox"
                                                                    checked={isSelected(doc)}
                                                                    onChange={() => toggleDoc(doc)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    readOnly
                                                                />
                                                                <i className="fas fa-folder convert-doc-icon"></i>
                                                                <span className="convert-doc-name" title={space.name}>{space.name}</span>
                                                                {space.description && (
                                                                    <span className="convert-doc-desc" title={space.description}>{space.description}</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                )
                                            ) : (
                                                currentPages.length === 0 ? (
                                                    <p className="convert-empty">No pages found.</p>
                                                ) : (
                                                    currentPages.map((page) => {
                                                        const doc = { type: 'page', id: page.id, name: page.page_name };
                                                        return (
                                                            <div
                                                                key={page.id}
                                                                className={`convert-doc-item ${isSelected(doc) ? 'selected' : ''}`}
                                                                onClick={() => toggleDoc(doc)}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    className="convert-doc-checkbox"
                                                                    checked={isSelected(doc)}
                                                                    onChange={() => toggleDoc(doc)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    readOnly
                                                                />
                                                                <i className="fas fa-file-alt convert-doc-icon"></i>
                                                                <span className="convert-doc-name" title={page.page_name}>{page.page_name}</span>
                                                                {page.description && (
                                                                    <span className="convert-doc-desc" title={page.description}>{page.description}</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                )
                                            )}
                                        </div>
                                    </>
                                )}

                                {selectedDocs.length > 0 && (
                                    <div className="convert-selected-count">
                                        {selectedDocs.length} document{selectedDocs.length !== 1 ? 's' : ''} selected
                                        {selectedDocs.filter(d => d.type === 'page').length > 0 && (
                                            <span className="convert-pages-note"> · parent assignment required for pages</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Target workspace (personal → workspace) */}
                            {direction === 'personal_to_workspace' && (
                                <div className="convert-workspace-section">
                                    <span className="convert-section-label">Target Workspace</span>
                                    <select
                                        className="convert-workspace-select"
                                        value={targetWorkspaceId}
                                        onChange={(e) => setTargetWorkspaceId(e.target.value)}
                                    >
                                        <option value="">Select a workspace…</option>
                                        {workspaces.map((ws) => (
                                            <option key={ws.id} value={ws.id}>{ws.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Options */}
                            <div className="convert-options-section">
                                <label className="convert-option-row">
                                    <input
                                        type="checkbox"
                                        className="convert-option-checkbox"
                                        checked={deleteOriginals}
                                        onChange={(e) => setDeleteOriginals(e.target.checked)}
                                    />
                                    <div>
                                        <span className="convert-option-label">
                                            Remove originals after conversion
                                        </span>
                                        <span className="convert-option-hint">
                                            Original documents will be permanently deleted once converted.
                                        </span>
                                    </div>
                                </label>
                            </div>

                            {error && <div className="convert-error">{error}</div>}
                        </div>

                        {/* Footer */}
                        <div className="convert-modal-footer">
                            <button className="convert-btn secondary" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                className="convert-btn primary"
                                onClick={handleNextOrConvert}
                                disabled={checkingChildren || selectedDocs.length === 0}
                            >
                                {checkingChildren ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Checking…</>
                                ) : selectedDocs.some(d => d.type === 'page') ? (
                                    <><i className="fas fa-arrow-right"></i> Next</>
                                ) : (
                                    <><i className="fas fa-exchange-alt"></i> Convert{selectedDocs.length > 0 ? ` (${selectedDocs.length})` : ''}</>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
}

export default ConvertDocumentModal;
